import React from 'react';
import { useTranslation } from 'react-i18next';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import TimeInput24h from '@/components/page-components/project/agents/ui/TimeInput24h';
import type { BusinessHour } from '@/services/api';
import {
  getEarliestEndTimeAfter,
  isBusinessHourRangeValid,
  mergeBusinessHours,
} from '@/utils/availability';
import { CardTitle } from '@/components/ui/card';

interface BusinessHoursProps {
  agentId?: number;
  value?: BusinessHour[];
  onChange?: (hours: BusinessHour[]) => void;
  disabled?: boolean;
  isLoading?: boolean;
  /** Tighter layout for narrow containers (e.g. new agent onboarding) */
  compact?: boolean;
}

// ISO weekday number -> translation key. The number is the stored value and
// must not change; only the label is language-dependent.
const DAYS_OF_WEEK = [
  {
    value: 1,
    labelKey: 'common.weekdays.monday.translation',
    fallback: 'Monday',
  },
  {
    value: 2,
    labelKey: 'common.weekdays.tuesday.translation',
    fallback: 'Tuesday',
  },
  {
    value: 3,
    labelKey: 'common.weekdays.wednesday.translation',
    fallback: 'Wednesday',
  },
  {
    value: 4,
    labelKey: 'common.weekdays.thursday.translation',
    fallback: 'Thursday',
  },
  {
    value: 5,
    labelKey: 'common.weekdays.friday.translation',
    fallback: 'Friday',
  },
  {
    value: 6,
    labelKey: 'common.weekdays.saturday.translation',
    fallback: 'Saturday',
  },
  {
    value: 7,
    labelKey: 'common.weekdays.sunday.translation',
    fallback: 'Sunday',
  },
];

const BusinessHours: React.FC<BusinessHoursProps> = ({
  agentId,
  value,
  onChange,
  disabled = false,
  isLoading = false,
  compact = false,
}) => {
  const { t } = useTranslation();

  const businessHours =
    value && value.length > 0 ? value : mergeBusinessHours(agentId ?? 0, []);

  const isWaitingForData =
    Boolean(agentId) && (isLoading || !value || value.length === 0);

  const handleDayChange = (
    dayOfWeek: number,
    updates: Partial<BusinessHour>
  ) => {
    const updated = businessHours.map(hour => {
      if (Number(hour.day_of_week) !== dayOfWeek) return hour;

      const next = { ...hour, ...updates, agent_id: agentId || hour.agent_id };
      if (
        !next.is_closed &&
        'start_time' in updates &&
        updates.start_time != null &&
        !isBusinessHourRangeValid(updates.start_time, next.end_time)
      ) {
        next.end_time = getEarliestEndTimeAfter(updates.start_time, 1);
      }
      return next;
    });
    onChange?.(updated);
  };

  return (
    <div className="space-y-4">
      <div>
        <CardTitle
          description={t(
            'agent.business_hours.description.translation',
            'Check days when open; uncheck when closed. Set hours for open days.'
          )}
        >
          {t('agent.business_hours.title.translation', 'Business Hours')}
        </CardTitle>
      </div>

      {isWaitingForData ? (
        <p className="text-sm text-muted-foreground">
          {t('common.loading.translation', 'Loading...')}
        </p>
      ) : (
        <div className="space-y-3">
          {DAYS_OF_WEEK.map(day => {
            const dayHours = businessHours.find(
              h => Number(h.day_of_week) === day.value
            );
            if (!dayHours) return null;

            return (
              <div
                key={day.value}
                className={
                  compact
                    ? 'flex flex-col items-stretch gap-2 rounded-md bg-card px-2 py-2.5 sm:flex-row sm:items-center sm:gap-3 sm:px-3 sm:py-3'
                    : 'flex flex-col items-start gap-3 rounded-md bg-card p-3 sm:flex-row sm:items-center'
                }
              >
                <div
                  className={
                    compact
                      ? 'flex min-w-0 items-center gap-1.5 sm:w-[6.25rem] sm:shrink-0'
                      : 'flex min-w-[120px] items-center gap-2'
                  }
                >
                  <Checkbox
                    id={`open-${day.value}`}
                    checked={!dayHours.is_closed}
                    onCheckedChange={checked =>
                      handleDayChange(day.value, {
                        is_closed: checked !== true,
                      })
                    }
                    disabled={disabled}
                    className="border-input shrink-0"
                  />
                  <Label
                    htmlFor={`open-${day.value}`}
                    className="min-w-0 flex-1 cursor-pointer text-sm font-medium leading-tight"
                  >
                    {t(day.labelKey, day.fallback)}
                  </Label>
                </div>

                {!dayHours.is_closed && (
                  <div
                    className={
                      compact
                        ? 'flex min-w-0 items-center justify-end gap-1.5 sm:flex-1 sm:justify-start sm:gap-2'
                        : 'flex flex-1 items-center gap-2'
                    }
                  >
                    <TimeInput24h
                      value={dayHours.start_time}
                      onChange={time =>
                        handleDayChange(day.value, { start_time: time })
                      }
                      disabled={disabled}
                      minuteStep={1}
                      className={
                        compact
                          ? 'shrink-0 [&_button]:h-8 [&_button]:w-[3.25rem] [&_button]:px-1.5'
                          : undefined
                      }
                    />
                    <span className="shrink-0 text-muted-foreground">-</span>
                    <TimeInput24h
                      value={dayHours.end_time}
                      onChange={time =>
                        handleDayChange(day.value, { end_time: time })
                      }
                      disabled={disabled}
                      minuteStep={1}
                      minTime={dayHours.start_time}
                      className={
                        compact
                          ? 'shrink-0 [&_button]:h-8 [&_button]:w-[3.25rem] [&_button]:px-1.5'
                          : undefined
                      }
                    />
                  </div>
                )}

                {dayHours.is_closed && (
                  <div className="flex-1 text-sm text-muted-foreground">
                    {t('agent.business_hours.closed.translation', 'Closed')}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default BusinessHours;
